import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, PenSquare, X } from 'lucide-react';
import AppHeader from '@/components/layout/AppHeader';
import IconRail from '@/components/layout/IconRail';
import Sidebar from '@/components/layout/Sidebar';
import MailList from '@/components/layout/MailList';
import MailView from '@/components/layout/MailView';
import ScheduledList from '@/components/layout/ScheduledList';
import OnboardingTour from '@/components/OnboardingTour';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Button } from '@/components/ui/button';
import useFolderCounts from '@/hooks/useFolderCounts';
import useLabels from '@/hooks/useLabels';
import LabelDialog from '@/components/labels/LabelDialog';
import { isLabelFolder, labelIdOfFolder } from '@/lib/labels';
import { parseRoute, mailPath, settingsPath, pagePath } from '@/lib/routes';
import { textToHtml } from '@/lib/compose';
import ContactsPage from './Contacts';
import CalendarPage from './Calendar';
import SettingsPage from './Settings';
import BulkMailPage from './BulkMail';

// Zengin metin editörü (TipTap) sadece e-posta yazılırken indirilir; ilk açılış paketi büyümesin
const ComposePage = lazy(() => import('@/components/compose/ComposePage'));

const toAddressList = (list) => (list || []).map((a) => ({ name: a.name || a.email, email: a.email }));

export const Dashboard = ({ user, onLogout }) => {
  // Adres çubuğu tek kaynaktır: sayfa / klasör / açık mail / ayar bölümü URL'den okunur (geri-ileri ve yenileme çalışır)
  const location = useLocation();
  const navigate = useNavigate();
  const route = parseRoute(location.pathname);
  const currentPage = route.page; // mail | compose | contacts | calendar | bulk | settings
  const currentFolder = route.folder;
  const selectedMailId = route.mailId;
  const settingsSection = route.section;
  const showMailView = Boolean(selectedMailId);
  const lastMailPathRef = useRef(mailPath('inbox'));
  useEffect(() => {
    if (route.page === 'mail') lastMailPathRef.current = mailPath(route.folder);
  }, [route.page, route.folder]);
  const [composeSession, setComposeSession] = useState(null); // { key, initialData } | null
  const [composeSubject, setComposeSubject] = useState('');
  const [isFolderPanelOpen, setIsFolderPanelOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mailIds, setMailIds] = useState([]);
  const [reloadToken, setReloadToken] = useState(0);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const composeKeyRef = useRef(0);

  // Klasör sayaçları, yeni mail bildirimi ve disk kotası (tüm sayfalarda aktif kalır)
  const { counts, quota, refreshCounts } = useFolderCounts();
  const { labels, labelsLoaded, reloadLabels } = useLabels();

  // Tanınmayan / eksik adresleri (ör. "/") kurallı adrese yönlendir; açık taslak yokken /compose'dan çık
  useEffect(() => {
    if (route.canonical && route.canonical !== location.pathname) navigate(route.canonical, { replace: true });
  }, [route.canonical, location.pathname, navigate]);

  useEffect(() => {
    if (currentPage === 'compose' && !composeSession) navigate(lastMailPathRef.current, { replace: true });
  }, [currentPage, composeSession, navigate]);

  // Açık etiket görünümünün etiketi silindiyse gelen kutusuna dön
  useEffect(() => {
    if (labelsLoaded && isLabelFolder(currentFolder) && !labels.some((l) => l.id === labelIdOfFolder(currentFolder))) {
      navigate(mailPath('inbox'), { replace: true });
    }
  }, [labels, labelsLoaded, currentFolder, navigate]);

  const handleLabelsChanged = async () => {
    await reloadLabels();
    setReloadToken((t) => t + 1);
  };

  useEffect(() => {
    const checkMobile = () => setIsMobileView(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ---- Gezinme ----
  // Zaten o adresteyse yeniden gezinme yapma (geçmişe aynı adres eklenmesin, render döngüsü olmasın)
  const goTo = (path, options) => {
    if (path !== location.pathname) navigate(path, options);
  };

  const handleNavigate = (page) => {
    goTo(page === 'mail' ? lastMailPathRef.current : pagePath(page));
    setIsMobileMenuOpen(false);
  };

  const handleFolderChange = (folder) => {
    navigate(mailPath(folder));
    setSearchQuery('');
    setIsMobileMenuOpen(false);
  };

  const handleOpenSettings = (section) => {
    goTo(settingsPath(section));
    setIsMobileMenuOpen(false);
  };

  const handleToggleSidebar = () => {
    if (isMobileView) setIsMobileMenuOpen((open) => !open);
    else setIsFolderPanelOpen((open) => !open);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (currentPage !== 'mail') navigate(lastMailPathRef.current);
  };

  // ---- Mail seçimi ----
  // Listeden ilk kez mail açılınca geçmişe eklenir (geri tuşu listeye döner); mailler arası geçişte yerini değiştirir
  const handleSelectMail = (mailId) => {
    if (selectedMailId) navigate(mailPath(currentFolder, mailId), { replace: true, state: location.state });
    else navigate(mailPath(currentFolder, mailId), { state: { fromList: true } });
  };

  const handleBackFromMail = () => {
    if (location.state?.fromList) navigate(-1);
    else navigate(mailPath(currentFolder), { replace: true });
  };

  const openAdjacentMail = (id) => navigate(mailPath(currentFolder, id), { replace: true, state: location.state });

  const currentIndex = mailIds.indexOf(selectedMailId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < mailIds.length - 1;

  // MailView'da silme/arşivleme/taşıma/yıldız sonrası: listeyi yenile, kaldırıldıysa seçimi temizle
  const handleMailChanged = ({ removed }) => {
    if (removed) handleBackFromMail();
    setReloadToken((t) => t + 1);
  };

  const handleMailsRemoved = (ids) => {
    if (ids.includes(selectedMailId)) handleBackFromMail();
  };

  // ---- Yeni e-posta (tam sayfa) ----
  // Tek açık oturum vardır. Yeni içerikle (yanıt/ilet/taslak) açılırsa mevcut taslak
  // ComposePage kapanırken otomatik kaydedilir.
  const handleCompose = (data = null) => {
    setIsMobileMenuOpen(false);
    if (composeSession) {
      if (!data) {
        navigate(pagePath('compose')); // açık taslağa dön
        return;
      }
      if (!window.confirm('Açık bir e-posta taslağınız var. Yenisini açarsanız mevcut taslak kaydedilip kapatılır. Devam edilsin mi?')) return;
    }
    composeKeyRef.current += 1;
    setComposeSession({ key: composeKeyRef.current, initialData: data });
    setComposeSubject(data?.subject || '');
    navigate(pagePath('compose'));
  };

  const handleComposeClosed = () => {
    setComposeSession(null);
    setComposeSubject('');
    if (currentPage === 'compose') navigate(lastMailPathRef.current, { replace: true });
    setReloadToken((t) => t + 1); // Gönderilenler / Taslaklar güncellensin
    refreshCounts();
  };

  // Taslaklar klasöründeki bir taslağı düzenlemek üzere aç
  const handleEditDraft = (email) =>
    handleCompose({
      mode: 'draft',
      draftUid: email.id,
      to: toAddressList(email.to),
      cc: toAddressList(email.cc),
      bcc: toAddressList(email.bcc),
      subject: email.subject || '',
      bodyHtml: email.body_html || textToHtml(email.body_text || ''),
      attachments: email.attachments || [],
    });

  const renderPageContent = () => {
    switch (currentPage) {
      case 'contacts':
        return <ContactsPage />;
      case 'calendar':
        return <CalendarPage onCompose={handleCompose} />;
      case 'settings':
        return <SettingsPage activeSection={settingsSection} onSectionChange={handleOpenSettings} onBack={() => handleNavigate('mail')} />;
      case 'bulk':
        return <BulkMailPage user={user} />;
      default:
        if (currentFolder === 'scheduled') return <ScheduledList onChanged={refreshCounts} />;
        return (
          <>
            {(!isMobileView || !showMailView) && (
              <MailList
                currentFolder={currentFolder}
                selectedMailId={selectedMailId}
                onSelectMail={handleSelectMail}
                isMobile={isMobileView}
                searchQuery={searchQuery}
                unreadCount={counts.inbox ?? null}
                reloadToken={reloadToken}
                onEmailsChange={setMailIds}
                onMailsRemoved={handleMailsRemoved}
                labels={labels}
                onManageLabels={() => setLabelDialogOpen(true)}
              />
            )}
            {(!isMobileView || showMailView) && (
              <MailView
                selectedMailId={selectedMailId}
                currentFolder={currentFolder}
                onBack={handleBackFromMail}
                isMobile={isMobileView}
                onCompose={handleCompose}
                onEditDraft={handleEditDraft}
                user={user}
                onChanged={handleMailChanged}
                labels={labels}
                onManageLabels={() => setLabelDialogOpen(true)}
                onPrev={() => hasPrev && openAdjacentMail(mailIds[currentIndex - 1])}
                onNext={() => hasNext && openAdjacentMail(mailIds[currentIndex + 1])}
                hasPrev={hasPrev}
                hasNext={hasNext}
              />
            )}
          </>
        );
    }
  };

  const folderPanelProps = {
    currentFolder,
    onFolderChange: handleFolderChange,
    onCompose: () => handleCompose(null),
    counts,
    quota,
    labels,
    onManageLabels: () => { setIsMobileMenuOpen(false); setLabelDialogOpen(true); },
    user,
  };

  const showFolderPanel = currentPage === 'mail' || currentPage === 'compose';

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background" data-testid="dashboard">
      <AppHeader
        user={user}
        onToggleSidebar={handleToggleSidebar}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onOpenSettings={handleOpenSettings}
        onLogout={onLogout}
      />

      <div className="flex-1 flex overflow-hidden">
        {!isMobileView && <IconRail currentPage={currentPage} onNavigate={handleNavigate} user={user} />}

        {!isMobileView && showFolderPanel && isFolderPanelOpen && <Sidebar {...folderPanelProps} />}

        <main className="flex-1 min-w-0 flex overflow-hidden">
          {currentPage !== 'compose' && (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPage}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="flex-1 min-w-0 flex overflow-hidden"
              >
                {renderPageContent()}
              </motion.div>
            </AnimatePresence>
          )}

          {/* E-posta yazma sayfası: küçültülünce gizlenir ama bellekte kalır (taslak/editör durumu korunur) */}
          {composeSession && (
            <div className={currentPage === 'compose' ? 'flex-1 min-w-0 flex overflow-hidden' : 'hidden'}>
              <ErrorBoundary
                key={composeSession.key}
                fallback={(error, reset) => (
                  <div className="flex-1 flex items-center justify-center p-8 text-center" data-testid="compose-error">
                    <div className="max-w-sm">
                      <h2 className="text-lg font-semibold mb-2">E-posta ekranı açılamadı</h2>
                      <p className="text-sm text-muted-foreground mb-4">
                        Sayfa yüklenirken bir sorun oluştu. Bağlantınızı kontrol edip tekrar deneyin.
                      </p>
                      <div className="flex justify-center gap-2">
                        <Button variant="outline" onClick={() => { reset(); handleComposeClosed(); }}>Kapat</Button>
                        <Button onClick={() => window.location.reload()}>Sayfayı yenile</Button>
                      </div>
                    </div>
                  </div>
                )}
              >
                <Suspense
                  fallback={<div className="flex-1 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>}
                >
                  <ComposePage
                    initialData={composeSession.initialData}
                    user={user}
                    onClose={handleComposeClosed}
                    onMinimize={() => navigate(lastMailPathRef.current)}
                    onSubjectChange={setComposeSubject}
                    onOpenSettings={handleOpenSettings}
                  />
                </Suspense>
              </ErrorBoundary>
            </div>
          )}
        </main>
      </div>

      {/* Küçültülmüş taslak çipi */}
      {composeSession && currentPage !== 'compose' && (
        <div
          className="fixed bottom-4 right-4 z-30 flex items-center rounded-full border border-border bg-card shadow-lg overflow-hidden"
          data-testid="compose-chip"
        >
          <button
            onClick={() => navigate(pagePath('compose'))}
            className="flex items-center gap-2 pl-4 pr-3 py-2.5 text-sm hover:bg-secondary transition-colors max-w-[260px]"
            title="E-postaya devam et"
          >
            <PenSquare className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">{composeSubject.trim() || 'Yeni e-posta'}</span>
            <span className="text-primary font-medium shrink-0">Devam et</span>
          </button>
          <button
            onClick={handleComposeClosed}
            aria-label="Taslağı kapat"
            title="Kapat (taslak kaydedilir)"
            className="px-3 py-2.5 border-l border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
            data-testid="compose-chip-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Mobil menü çekmecesi */}
      <AnimatePresence>
        {isMobileView && isMobileMenuOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              key="drawer"
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'tween', duration: 0.2 }}
              className="fixed inset-y-0 left-0 z-50 w-[280px] shadow-xl"
            >
              <Sidebar
                {...folderPanelProps}
                showNav
                currentPage={currentPage}
                onNavigate={handleNavigate}
                className="!w-full h-full"
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <LabelDialog open={labelDialogOpen} onOpenChange={setLabelDialogOpen} labels={labels} onChanged={handleLabelsChanged} />

      <OnboardingTour onNavigate={handleNavigate} onSettingsSection={handleOpenSettings} />
    </div>
  );
};

export default Dashboard;
